import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { ApiError } from '@shared/api/client';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { ConfirmDialog, type ConfirmSubject } from '@shared/ui/dialog';
import { EmptyState } from '@shared/ui/EmptyState';
import { ListSearch } from '@shared/ui/ListSearch';
import { ErrorBanner } from '@shared/ui/ErrorBanner';
import { PlusIcon } from '@shared/ui/icons';
import { RowSkeleton, DTable, DTableBody, DTableHead, DTableScroll } from '@shared/ui/dtable';
import { cn } from '@shared/ui/cn';

import { Pager } from '@shared/ui/pager';

import { deleteCopy, stateLabels, unpublishCopy } from '../_shared/copy';
import { editorialErrors } from '../_shared/errors';
import { postsKey, usePermissions, usePosts } from '../_shared/queries';
import { useDeletePost, usePublishPost, useRejectPost, useWriterTransition } from '../_shared/mutations';
import { RejectDialog } from '../_shared/RejectDialog';
import {
  EDITORIAL_STATES,
  STATE_FILTERS,
  withdrawUnpublishes,
  type PostAction,
  type StateFilter,
} from '../_shared/states';

/**
 * How many pages the total divides into, or `undefined` when the service
 * declared a page size of zero — a figure the console must not divide by, and
 * one it must not silently replace with a guess either.
 */
function pageCount(total: number, pageSize: number): number | undefined {
  if (pageSize <= 0) return undefined;
  return Math.max(1, Math.ceil(total / pageSize));
}
import { holdsReviewerRole, type EditorialPostRow } from '../_shared/types';
import { copy } from './copy';
import { PostTable, TABLE_COLUMNS, postHref } from './Editorial.PostTable';

/**
 * Editorial management — every post across the four-state lifecycle, the filter
 * that **is** the review queue, the transitions available per row, and the
 * route into authoring.
 *
 * **No second route for the queue.** Working the review queue is, in practice,
 * this list filtered to «بانتظار المراجعة»; a route of its own would rebuild
 * the same table behind one condition and leave an operator two places to look
 * for one post.
 *
 * **Every chip count is read from the table, never written.** A number typed
 * into a chip is wrong the moment a state changes, and changing state is the
 * first thing an operator does here. A chip whose state holds nothing stays
 * visible and disabled: a strip that changes length on every transition hides
 * that the state exists at all.
 *
 * **A deleted row leaves the table, because the post left the system.** Delete
 * is hard: the row, its links and its review history are destroyed, so the list
 * refetches without it. Keeping the row behind a «محذوف» badge is what the
 * earlier soft delete did, and it promised a restore this console no longer has.
 */
export function EditorialPage() {
  const [params, setParams] = useSearchParams();

  const term = params.get('q') ?? '';
  const filter = readFilter(params.get('state'));
  const page = Math.max(1, Number(params.get('page')) || 1);

  const query = usePosts(page, term, filter);
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const writerTransition = useWriterTransition();
  const deletePost = useDeletePost();
  const publishPost = usePublishPost();
  const rejectPost = useRejectPost();

  const [direction, setDirection] = useState<'ascending' | 'descending'>('descending');
  const [deletion, setDeletion] = useState<EditorialPostRow | null>(null);
  const [rejection, setRejection] = useState<EditorialPostRow | null>(null);
  const [unpublishing, setUnpublishing] = useState<EditorialPostRow | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [writeError, setWriteError] = useState<unknown>(null);

  /**
   * Every narrowing writes itself to the address bar.
   *
   * `replace` for the term and `push` for the page and the facet: typing is one
   * continuous act that should not leave a history entry per keystroke, while
   * turning a page and pressing a chip are steps a reader expects Back to undo.
   * A new term or a new facet restarts the traversal — keeping page 3 would open
   * on rows the narrowing may not have.
   */
  function updateParams(next: { q?: string; state?: StateFilter; page?: number }, replace: boolean) {
    const merged = new URLSearchParams(params);
    if (next.q !== undefined) {
      if (next.q) merged.set('q', next.q);
      else merged.delete('q');
      merged.delete('page');
    }
    if (next.state !== undefined) {
      if (next.state !== 'all') merged.set('state', next.state);
      else merged.delete('state');
      merged.delete('page');
    }
    if (next.page !== undefined) {
      if (next.page > 1) merged.set('page', String(next.page));
      else merged.delete('page');
    }
    setParams(merged, { replace });
  }

  const rows = useMemo(() => query.data?.items ?? [], [query.data]);
  const canReview = holdsReviewerRole(permissions.data);
  /** Whether anything is narrowing the list — it decides which empty state is true. */
  const narrowed = term.trim().length > 0 || filter !== 'all';

  /**
   * The chip figures, read off the response rather than tallied from the page.
   *
   * A tally over the rows in hand counted one page and labelled the whole list —
   * «٣» beside «بانتظار المراجعة» over a backlog of thirty. `state_counts` is
   * counted by the service under the search term alone, so «الكلّ» is the term's
   * whole total and each state's chip is what pressing it would show.
   *
   * A console deployed ahead of the service that serves the key falls back to
   * the page tally rather than printing nothing: a stale figure beside a table
   * is degraded, an empty chip strip hides that the states exist.
   */
  const counts = useMemo(() => {
    const tally = new Map<StateFilter, number>();
    const served = query.data?.state_counts;

    if (served) {
      tally.set('all', query.data?.total ?? 0);
      for (const state of EDITORIAL_STATES) tally.set(state, served[state] ?? 0);
      return tally;
    }

    tally.set('all', rows.length);
    for (const post of rows) tally.set(post.state, (tally.get(post.state) ?? 0) + 1);
    return tally;
  }, [rows, query.data]);

  /**
   * The rows the table draws — **the service already applied the facet**, so
   * this is an ordering, not a filter.
   */
  const visible = useMemo(() => {
    // A post with no publication date holds the tail in both directions.
    return [...rows].sort((left, right) => {
      if (!left.publishedAt && !right.publishedAt) return 0;
      if (!left.publishedAt) return 1;
      if (!right.publishedAt) return -1;
      const delta = left.publishedAt < right.publishedAt ? -1 : left.publishedAt > right.publishedAt ? 1 : 0;
      return direction === 'descending' ? -delta : delta;
    });
  }, [rows, direction]);

  function settle(message: string) {
    setAnnouncement(message);
    setWriteError(null);
  }

  function onAction(action: PostAction, post: EditorialPostRow) {
    setWriteError(null);

    if (action === 'open') {
      void navigate(postHref(post));
      return;
    }

    if (action === 'edit') {
      void navigate(`${postHref(post)}/edit`);
      return;
    }

    if (action === 'delete') {
      setDeletion(post);
      return;
    }

    if (action === 'reject') {
      setRejection(post);
      return;
    }

    // Withdrawing a published post takes a live article off the public site the
    // moment it lands, from a menu one entry away from «احذف المقال». Every
    // other withdrawal is a recall of something readers never saw and goes
    // straight through.
    if (action === 'withdraw' && withdrawUnpublishes(post.state)) {
      setUnpublishing(post);
      return;
    }

    if (action === 'approve') {
      publishPost.mutate(post.id, {
        onSuccess: () => settle(copy.announce.published(post.title)),
        onError: setWriteError,
      });
      return;
    }

    const transition = action === 'submit' ? 'submit' : 'withdraw';
    const announce =
      action === 'submit' ? copy.announce.submitted(post.title) : copy.announce.withdrawn(post.title);

    writerTransition.mutate(
      { postId: post.id, transition },
      { onSuccess: () => settle(announce), onError: setWriteError },
    );
  }

  const confirmSubject: ConfirmSubject | null = deletion
    ? {
        heading: deleteCopy.heading(deletion.title),
        body: deleteCopy.body,
        commitLabel: deleteCopy.commit,
        intent: 'danger',
      }
    : null;

  /**
   * `warning`, not `danger`: the post is removed from readers and stays whole,
   * and spending the danger treatment on a reversible act cheapens it on the
   * deletion sitting beside it in the same menu.
   */
  const unpublishSubject: ConfirmSubject | null = unpublishing
    ? {
        heading: unpublishCopy.heading(unpublishing.title),
        body: unpublishCopy.body,
        commitLabel: unpublishCopy.commit,
        intent: 'warning',
      }
    : null;

  return (
    <>
      <Breadcrumb items={[{ label: copy.crumbs.root, to: '/' }, { label: copy.crumbs.current }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <section aria-labelledby="editorial-posts">
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 id="editorial-posts" className="font-display text-h2 font-bold">
            {copy.section.heading}
          </h2>
          <Link
            to="/editorial/new"
            className="ms-auto flex min-h-6 items-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink no-underline hover:bg-surface-overlay"
          >
            <PlusIcon className="flex-none" />
            <span>{copy.section.add}</span>
          </Link>
        </div>

        <p className="mb-4 mt-3 max-w-measure text-xs text-ink-muted">{copy.section.lifecycle}</p>

        <ListSearch
          label={copy.search.label}
          placeholder={copy.search.placeholder}
          hint={copy.search.hint}
          value={term}
          onChange={(next) => updateParams({ q: next }, true)}
        />

        <div role="group" aria-label={copy.facet.groupLabel} className="mb-4 grid gap-2">
          <p className="mb-0 text-xs text-ink-muted">{copy.facet.label}</p>
          <div className="flex flex-wrap gap-2">
            {STATE_FILTERS.map((value) => {
              const count = counts.get(value) ?? 0;
              const active = filter === value;

              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  disabled={value !== 'all' && count === 0}
                  onClick={() => updateParams({ state: value }, false)}
                  className={cn(
                    'inline-flex min-h-[2.25rem] items-center gap-2 rounded-pill border px-4 text-sm disabled:cursor-not-allowed disabled:opacity-45',
                    active
                      ? 'border-accent bg-accent font-semibold text-accent-ink'
                      : 'border-border-strong bg-transparent text-ink hover:bg-hover-bg',
                  )}
                >
                  {value === 'all' ? copy.facet.all : stateLabels[value]}
                  <span className="inline-flex items-center rounded-pill bg-surface-overlay px-[0.55em] py-[0.2em] text-xs text-ink-muted [font-variant-numeric:tabular-nums]">
                    <bdi>{formatNumber(count)}</bdi>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {writeError ? (
          <div className="mb-4">
            <ErrorBanner {...resolve(writeError)} />
          </div>
        ) : null}

        {query.isError ? (
          <ErrorBanner
            {...resolve(query.error)}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: postsKey(page, term, filter) })}
          />
        ) : null}

        {query.isPending ? (
          <>
            <p role="status" className="sr-only">
              {copy.states.loading}
            </p>
            <DTableScroll>
              <DTable caption={copy.section.caption}>
                <DTableHead>
                  <tr>
                    <th scope="col">{copy.columns.post}</th>
                    <th scope="col">{copy.columns.state}</th>
                    <th scope="col">{copy.columns.author}</th>
                    <th scope="col">{copy.columns.date}</th>
                    <th scope="col" className="[inline-size:3.5rem]" />
                  </tr>
                </DTableHead>
                <DTableBody>
                  <RowSkeleton columns={TABLE_COLUMNS} />
                </DTableBody>
              </DTable>
            </DTableScroll>
          </>
        ) : null}

        {query.data ? (
          rows.length === 0 ? (
            narrowed ? (
              /*
                §9.16's three parts — what happened, why, and the way back.

                **The narrowed empty state is not the empty list.** The service
                answered zero rows for this term and this state; saying «لا مقالات
                بعد» there would claim the section holds nothing, which is a
                different and false statement.
              */
              <div className="my-4 grid justify-items-start gap-3 rounded-md border border-border bg-bg-sunken p-6">
                <p className="mb-0 max-w-none text-sm text-ink-muted">
                  {term ? copy.emptyFilter.searched(term) : copy.emptyFilter.message}
                </p>
                <button
                  type="button"
                  onClick={() => updateParams({ q: '', state: 'all' }, false)}
                  className="min-h-6 rounded-sm border border-border-strong bg-surface-raised px-3 text-xs font-semibold text-ink hover:bg-surface-overlay"
                >
                  {copy.emptyFilter.reset}
                </button>
              </div>
            ) : (
              <EmptyState message={copy.states.empty} />
            )
          ) : (
            <PostTable
              rows={visible}
              direction={direction}
              onToggleSort={() =>
                setDirection((current) => (current === 'descending' ? 'ascending' : 'descending'))
              }
              canReview={canReview}
              onAction={onAction}
            />
          )
        ) : null}

        {/*
          Outside the row check on purpose: a page walked one past the end has no
          rows and the control that walks back is the only way off it.

          `shown` counts the rows the facet left standing, not the rows the page
          carried — the operator is looking at the first figure, and a pager
          reporting the second beside a table showing the first would be two
          numbers for one table.
        */}
        {query.data ? (
          <Pager
            label={copy.pager.label}
            page={page}
            pageCount={pageCount(query.data.total, query.data.page_size)}
            shown={visible.length}
            total={query.data.total}
            noun={{
              one: copy.pager.shownOne,
              two: copy.pager.shownTwo,
              few: copy.pager.shownFew,
              many: copy.pager.shownMany,
            }}
            hasPrevious={page > 1}
            hasNext={query.data.has_next}
            onPrevious={() => updateParams({ page: Math.max(1, page - 1) }, false)}
            onNext={() => updateParams({ page: page + 1 }, false)}
            onGoToPage={(target) => updateParams({ page: target }, false)}
          />
        ) : null}
      </section>

      <RejectDialog
        open={rejection !== null}
        {...(rejection ? { authorName: rejection.author.displayName } : {})}
        busy={rejectPost.isPending}
        onReject={(reason) => {
          if (!rejection) return;
          const post = rejection;
          setRejection(null);
          rejectPost.mutate(
            { postId: post.id, reason },
            { onSuccess: () => settle(copy.announce.rejected(post.title)), onError: setWriteError },
          );
        }}
        onCancel={() => setRejection(null)}
      />

      <ConfirmDialog
        open={unpublishing !== null}
        subject={unpublishSubject}
        busy={writerTransition.isPending}
        onConfirm={() => {
          if (!unpublishing) return;
          const post = unpublishing;
          setUnpublishing(null);
          writerTransition.mutate(
            { postId: post.id, transition: 'withdraw' },
            { onSuccess: () => settle(copy.announce.unpublished(post.title)), onError: setWriteError },
          );
        }}
        onCancel={() => setUnpublishing(null)}
      />

      <ConfirmDialog
        open={deletion !== null}
        subject={confirmSubject}
        busy={deletePost.isPending}
        onConfirm={() => {
          if (!deletion) return;
          const post = deletion;
          setDeletion(null);
          deletePost.mutate(post.id, {
            onSuccess: () => settle(copy.announce.deleted(post.title)),
            onError: setWriteError,
          });
        }}
        onCancel={() => setDeletion(null)}
      />
    </>
  );
}

/**
 * The `state` query parameter, refused unless it names a state this console
 * knows. An address carrying a typo would otherwise be sent to the service as a
 * facet and answered 422 — an error page for a stale bookmark, where «الكلّ» is
 * the honest reading of an unrecognised narrowing.
 */
function readFilter(value: string | null): StateFilter {
  return STATE_FILTERS.includes(value as StateFilter) ? (value as StateFilter) : 'all';
}

function resolve(error: unknown) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, editorialErrors);

  return {
    message: resolved.message,
    tone: resolved.tone === 'danger' ? ('danger' as const) : ('neutral' as const),
  };
}
